import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  CloseOutlined,
  CopyAllOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { blue, grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AgButtonDownloadTable from "../../components/agGrid/AgButtonDownloadTable";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { isArray, isNil } from "lodash";
import PrimaryContentCard from "../../_src_shared/components/layout/PrimaryContentCard";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import { formatBorrowerObjectFullName } from "../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatPhone } from "../../_src_shared/utils/functions/formatPhone";
import { convertObjectToReadableEntries } from "../../_src_shared/utils/objects/convertObjectToReadableEntries";
import CreateLoanFromAppButton from "../../components/common/buttons/CreateLoanFromAppButton";
import JsonPreview from "../../components/common/JsonPreview";
import LoanFilesDisplayTable from "../../components/common/Loan/tables/LoanFilesDisplayTable";
import RffForm from "../../components/finalForm/RffForm";
import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import TitledHeaderWithSearch from "../../components/layout/TitledHeaderWithSearch";
import UrlCopyPastModal from "../../components/modals/UrlCopyPastModal";
import TabContent from "../../components/navigation/TabContent";
import ToggleTabSwitcher from "../../components/navigation/ToggleTabSwitcher";
import PdfDownloadButton from "../../components/pdf/components/PdfDownloadButton";
import PdfPreviewer from "../../components/pdf/components/PdfPreviewer";
import GenericSubmittedApplicationPDF from "../../components/pdf/GenericSubmittedApplicationPDF";
import Txt from "../../components/typography/Txt";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { cachedDataItemSet } from "../../redux/actions/cachedDataActions";
import { colDefFilterMultiSelect } from "../../utils/agGrid/colDefs/colDefFilterMultiSelect";
import { sideBarColumnsFilters } from "../../utils/agGrid/sideBar/sideBarColumnsFilters";
import { valueFormatterBooleanYN } from "../../utils/agGrid/valueFormatter/valueFormatterBooleanYN";

const CACHED_DATA_KEY = "quickApp";
const TABS = {
  ALL_APPS: "All Quick Apps",
  MY_APPS: "My Quick Apps",
};
const LoanQuickApplicationPipelineScreen = () => {
  const cachedData = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY],
  );
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const [lastSynced, setLastSynced] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [tab, setTab] = useState(TABS.ALL_APPS);
  const [quickFilter, setQuickFilter] = useState(null);

  const { id } = useParams();
  const dispatch = useDispatch();
  const ref = useRef();

  const { getQuickApplications, loading } = useBorrowerSubmissionsHook();

  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  const filteredApps = useMemo(() => {
    if (tab === TABS.MY_APPS) {
      return (
        cachedData?.filter((app) => app?.loan_officer_id === user?._id) || []
      );
    }
    return cachedData;
  }, [tab, cachedData, user]);
  useEffect(() => {
    if (isArray(cachedData)) {
      return;
    }

    if (isNil(cachedData)) {
      getQuickApplications({
        onSuccessFn: (d) => {
          dispatch(cachedDataItemSet(CACHED_DATA_KEY, d));
          setLastSynced(new Date().toLocaleString());
        },
      });
    }
  }, [cachedData, loanPipeline]);

  const onRowClicked = useCallback((props) => {
    navigate(`/quick-applications/${props.data._id}`);
  }, []);
  return (
    <Screen>
      <TitledHeaderWithSearch
        title="Quick Applications"
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        titleEndContent={
          <Box sx={{ position: "relative" }}>
            <Flx
              ac
              gap={0.5}
              sx={{
                position: "absolute",
                width: "max-content",
                top: -14,
                left: 0,
              }}
            >
              <Tooltip title="Refresh Quick Applications">
                <IconButton
                  size="small"
                  onClick={() =>
                    dispatch(cachedDataItemSet(CACHED_DATA_KEY, null))
                  }
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SyncOutlined className="thin" />
                  )}
                </IconButton>
              </Tooltip>
              {lastSynced && <Txt secondary>Last Synced: {lastSynced}</Txt>}
            </Flx>
          </Box>
        }
        endContent={
          <Flx g={1}>
            <UrlCopyPastModal
              color="primary"
              url={`${window.location.origin}/quick-app?loan_officer_id=${user?._id}`}
              description={
                "Copy and share the following link to allow borrowers to fill out a quick application."
              }
              title={"Quick App Link"}
              size="small"
              endIcon={<CopyAllOutlined className="thin" />}
            >
              Copy Quick App Link
            </UrlCopyPastModal>

            <AgButtonDownloadTable ref={ref} downloadName={`Quick App`} />
          </Flx>
        }
      />
      <Flx
        sx={{
          borderTop: `1px solid ${grey[300]}`,
          borderBottom: `1px solid ${grey[300]}`,
          py: 0.4,
          px: 0.4,
        }}
        ac
        jb
      >
        <ToggleTabSwitcher
          value={tab}
          onChange={setTab}
          tabs={Object.values(TABS)}
        />

        <Flx ac g={2.4}>
          {/* <Button
            size="small"
            variant={"text"}
            color={showAll ? "primary" : "gray"}
            sx={{ fontWeight: showAll ? 700 : 500 }}
            startIcon={
              showAll ? (
                <CheckBoxOutlined />
              ) : (
                <CheckBoxOutlineBlankOutlined className="thin" />
              )
            }
            onClick={() => setShowAll((s) => !s)}
          >
            Show All Details & Columns
          </Button> */}
          <Button
            size="small"
            variant={"text"}
            color={showAll ? "primary" : "gray"}
            sx={{ fontWeight: showAll ? 700 : 500 }}
            startIcon={
              showAll ? (
                <CheckBoxOutlined />
              ) : (
                <CheckBoxOutlineBlankOutlined className="thin" />
              )
            }
            onClick={() => setShowAll((s) => !s)}
          >
            Show All Details & Columns
          </Button>
        </Flx>
      </Flx>

      <Flx sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
        <QuickApplicationsTable
          showAll={showAll}
          loading={loading}
          ref={ref}
          rowData={filteredApps}
          onRowClicked={onRowClicked}
          quickFilter={quickFilter}
        />
        {id && (
          <LoanApplicationPreviewPane
            onClose={() => navigate("/quick-applications")}
          />
        )}
      </Flx>
    </Screen>
  );
};

const LoanApplicationPreviewPane = ({ onClose }) => {
  const { id } = useParams();
  const { loading, createLoanFromApplication } = useUnderwritingHook();

  const cachedData = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY],
  );

  const quickApp = useMemo(() => {
    return cachedData?.find((app) => app._id === id);
  }, [id, cachedData]);

  const handleCreateLoanFromApp = useCallback(() => {
    createLoanFromApplication({
      applicationId: quickApp?._id,
    });
  }, [quickApp?._id]);

  const TABS = useMemo(() => {
    const BASE_TABS = {
      PREVIEW: "Quick App Preview",
      FILES: "Uploaded Files",
      // CREATE_LOAN: "Create Loan",
      // PUSH_TO_LOAN: "Push to Loan",
      RAW_JSON: "Raw JSON",
    };

    if (!quickApp?.documents || quickApp.documents.length === 0) {
      const { FILES, ...rest } = BASE_TABS;
      return rest;
    }

    return BASE_TABS;
  }, [quickApp?.documents]);

  const [tab, setTab] = useState(TABS.PREVIEW);
  const formatted = convertObjectToReadableEntries(quickApp, {
    format: [
      { field: "cashReserves", type: "dollar" },
      { field: "submission_date", type: "date" },
      { field: "purchaseDate", type: "date" },

      { field: "expectedClosingDate", type: "date" },
      { field: "purchasePrice", type: "dollar" },
      { field: "constructionBudget", type: "dollar" },
      { field: "asIsValue", type: "dollar" },
      { field: "annualTaxes", type: "dollar" },
      { field: "annualInsurance", type: "dollar" },
      { field: "annualHoaFees", type: "dollar" },
      { field: "additionalBasis", type: "dollar" },
      { field: "arv", type: "dollar" },
      { field: "totalMonthlyRent", type: "dollar" },
    ],
    excludeKeys: [
      "uploadMetadata",
      "_id",
      "client_ip",
      "loan_officer_id",
      "loan_officer_info",
      "query_params",
      "file_name_as_loaded",
      "file_storage_name",
      "size",
      "uploadedBy",
      "containerName",
      "_id",
      "content_type",
      "size",
      "coordinateTuple",
      "score",
      "latitude",
      "longitude",
      "id",
    ],
    // excludeKeys: ["borrowers", "subjectProperties", "uploadMetadata", "_id"],
  });

  return (
    <Flx
      column
      sx={{
        background: grey[200],
        width: "820px",
        overflow: "hidden",
        borderLeft: `2px solid ${grey[400]}`,
      }}
    >
      <Box
        sx={{
          background: "#ffffff",
          flexGrow: 0,
          p: 0.5,
          borderBottom: `1px solid ${grey[400]}`,
        }}
      >
        <Flx fw jb ac>
          <ToggleTabSwitcher
            tabs={Object.values(TABS)}
            value={tab}
            onChange={setTab}
          />
          <IconButton size="small" onClick={onClose}>
            <CloseOutlined />
          </IconButton>
        </Flx>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          height: "100%",
          background: grey[200],
          p: 2,
        }}
      >
        <TabContent activeTab={tab} tabKey={TABS.PREVIEW}>
          <PreviewPaneCard
            title={"Quick Application"}
            bodySx={{
              position: "relative",
              height: "100%",
            }}
            endContent={
              <RffForm suppressFooter initialValues={quickApp}>
                <Flx ac gap={1}>
                  <CreateLoanFromAppButton
                    data={quickApp}
                    typeKeyName={"quickApp"}
                    confirmationDialogTitle="Create Loan From Quick App"
                  />

                  <PdfDownloadButton
                    filename={`Quick_Application_${formatDate(new Date())}.pdf`}
                  >
                    <GenericSubmittedApplicationPDF
                      formData={formatted}
                      sortKeys
                      title="Quick Application"
                    />
                  </PdfDownloadButton>
                </Flx>
              </RffForm>
            }
          >
            <PdfPreviewer
              sx={{
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                minHeight: "720px",
              }}
            >
              <GenericSubmittedApplicationPDF
                formData={formatted}
                title="Quick Application"
              />
            </PdfPreviewer>
          </PreviewPaneCard>
        </TabContent>

        <TabContent activeTab={tab} tabKey={TABS.FILES}>
          <PreviewPaneCard title={"Uploaded App Files"}>
            <LoanFilesDisplayTable rowData={quickApp?.documents} />
          </PreviewPaneCard>
        </TabContent>
        <TabContent activeTab={tab} tabKey={TABS.RAW_JSON}>
          <PreviewPaneCard title={"Quick Application Raw JSON"}>
            <JsonPreview defaultCollapse={4} values={quickApp} />
          </PreviewPaneCard>
        </TabContent>
      </Box>
    </Flx>
  );
};

const PreviewPaneCard = ({
  title,
  subTitle,
  children,
  cardSx,
  bodySx,
  endContent,
}) => {
  return (
    <PrimaryContentCard
      title={title}
      subTitle={subTitle}
      endContent={endContent}
      cardSx={{ mb: 0, flexGrow: 1, height: "100%", ...cardSx }}
      bodySx={{
        position: "relative",
        height: "100%",
        ...bodySx,
      }}
    >
      {children}
    </PrimaryContentCard>
  );
};

const QuickApplicationsTable = forwardRef(
  (
    {
      quickFilter,
      rowData,
      showAll = false,
      onRowClicked,
      loading,
      activeApplication,
    },
    ref,
  ) => {
    const { id } = useParams();
    const columnTypes = useColumnTypes();
    const sideBar = useMemo(() => {
      if (showAll) {
        return sideBarColumnsFilters();
      }
      return null;
    }, [showAll]);

    const columnDefs = useMemo(
      () => [
        {
          headerName: "Submission Date",
          type: "date",
          field: "submission_date",
          width: 170,
          sort: "desc",
          minWidth: 170,
          flex: 0,
        },
        { headerName: "Loan Product Type", field: "loanProductType" },
        { headerName: "Loan Purpose", field: "loanPurpose" },
        {
          headerName: "Sent By Loan Officer",
          field: "loan_officer_name",
          valueFormatter: (p) => {
            if (p?.data?.loan_officer_name) {
              return p?.data?.loan_officer_name;
            }

            if (p?.data?.loan_officer_info.fullName) {
              return p?.data?.loan_officer_info?.fullName;
            }

            return "N/A";
          },
        },
        {
          headerName: "Borrower Name",
          valueGetter: (p) =>
            formatBorrowerObjectFullName(p.data?.borrowers?.[0]),
        },

        {
          headerName: "Borrower Email",
          valueGetter: (p) => p.data?.borrowers?.[0]?.emailAddress,
        },
        {
          headerName: "Borrower Phone",
          valueGetter: (p) => p.data?.borrowers?.[0]?.phone,
          valueFormatter: (p) => formatPhone(p.value),
        },
        {
          headerName: "Borrower Company",
          valueGetter: (p) => p.data?.borrowers?.[0]?.company,
        },

        {
          headerName: "Has Closed with Lender Before",
          valueGetter: (p) => p.data?.hasClosedWithLenderBefore,
          valueFormatter: valueFormatterBooleanYN,
        },
        {
          headerName: "Brokered Loan",
          valueGetter: (p) => p.data?.isBrokeredLoan,
          valueFormatter: (p) => valueFormatterBooleanYN(p.value),
        },
        {
          headerName: "Broker Name",
          field: "brokerFirstName",
          valueFormatter: (p) => {
            const filteredBroker = p?.data?.loanRelatedContacts?.find(
              (c) => c.loanRole === "Broker Contact",
            );
            return isNil(filteredBroker)
              ? "N/A"
              : `${filteredBroker?.firstName} ${filteredBroker?.lastName}`;
          },
        },

        {
          headerName: "Broker Company",
          field: "brokerCompany",
          hide: !showAll,
          valueFormatter: (p) => {
            const filteredBroker = p?.data?.loanRelatedContacts?.find(
              (c) => c.loanRole === "Broker Contact",
            );
            return isNil(filteredBroker) ? "N/A" : filteredBroker?.company;
          },
        },
        {
          headerName: "Broker Phone",
          field: "brokerPhone",
          valueFormatter: (p) => {
            const filteredBroker = p?.data?.loanRelatedContacts?.find(
              (c) => c.loanRole === "Broker Contact",
            );
            return isNil(filteredBroker)
              ? "N/A"
              : formatPhone(filteredBroker?.phone);
          },

          hide: !showAll,
        },
        {
          headerName: "Broker Email",
          field: "brokerEmail",
          valueFormatter: (p) => {
            const filteredBroker = p?.data?.loanRelatedContacts?.find(
              (c) => c.loanRole === "Broker Contact",
            );
            return isNil(filteredBroker) ? "N/A" : filteredBroker?.emailAddress;
          },
          hide: !showAll,
        },
        {
          headerName: "Similar Projects",
          hide: !showAll,
          valueGetter: (p) =>
            p.data?.borrowers?.[0]?.similar_projects_last_3_years,
        },
        {
          headerName: "Cash Reserves",
          hide: !showAll,
          valueGetter: (p) => p.data?.borrowers?.[0]?.cashLiquidity,
          type: "dollar",
        },
        { headerName: "Credit Score", hide: !showAll, field: "creditScore" },
        {
          headerName: "Referral Source",
          hide: !showAll,
          field: "referralSource",
        },
        {
          headerName: "Additional Details",
          hide: !showAll,
          field: "additionalDetails",
        },
        {
          hide: !showAll,
          headerName: "SMS Opt In",
          valueGetter: (p) => p.data?.smsOptIn,
          valueFormatter: (p) => valueFormatterBooleanYN(p.value),
        },

        //

        {
          hide: !showAll,
          headerName: "Full Address",
          valueGetter: (p) =>
            p.data?.subjectProperties?.[0]?.address?.fullAddress,
          minWidth: 260,
        },
        {
          hide: !showAll,
          headerName: "City",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.address?.city,
        },
        {
          hide: !showAll,
          headerName: "State",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.address?.state,
          maxWidth: 110,
        },
        {
          hide: !showAll,
          headerName: "Zip",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.address?.zip,
          maxWidth: 120,
        },
        {
          hide: !showAll,
          headerName: "County",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.address?.county,
        },
        {
          hide: !showAll,
          headerName: "Property Type",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.propertyType,
        },
        {
          hide: !showAll,
          headerName: "Purchase Price",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.purchasePrice,
          type: "dollar",
        },
        {
          hide: !showAll,
          headerName: "Construction Budget",
          valueGetter: (p) =>
            p.data?.subjectProperties?.[0]?.constructionBudget,
          type: "dollar",
        },
        {
          hide: !showAll,
          headerName: "ARV",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.arv,
          type: "dollar",
        },
        {
          hide: !showAll,
          headerName: "As-Is Value",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.asIsValue,
          type: "dollar",
        },
        {
          hide: !showAll,
          headerName: "Monthly Rents",
          valueGetter: (p) => p.data?.subjectProperties?.[0]?.totalMonthlyRent,
          type: "dollar",
        },
        {
          hide: !showAll,
          headerName: "Closing Requested",
          valueGetter: (p) =>
            p.data?.subjectProperties?.[0]?.expectedClosingDate,
          type: "date",
        },
        {
          hide: !showAll,
          headerName: "Sqft Changed?",
          valueGetter: (p) =>
            p.data?.subjectProperties?.[0]?.hasSqftAddedOrUseChanged,
          valueFormatter: (p) => valueFormatterBooleanYN(p.value),
        },
      ],
      [showAll],
    );

    const handleOnRowClicked = useCallback((props) => {
      onRowClicked(props);
    }, []);

    const defaultColDef = useMemo(() => {
      return {
        enableRowGroup: true,
        floatingFilter: false,
        filter: colDefFilterMultiSelect,
        showRowGroup: false,
        suppressHeaderFilterButton: true,
        suppressMenu: true,

        flex: 1,
        minWidth: 200,
        cellClass: "ag-cell-vertical-center",
      };
    }, []);

    const getRowStyle = useCallback(
      (props) => {
        if (id === props.data?._id) {
          return {
            backgroundColor: blue[50],
          };
        }
      },
      [id],
    );

    return (
      <AgGridTableWrapper
        height={"100%"}
        noBorder
        sx={{ flexGrow: 1, width: "auto" }}
      >
        <AgGridReact
          // {...gridOptions}
          rowData={rowData}
          ref={ref}
          columnDefs={columnDefs}
          getRowStyle={getRowStyle}
          quickFilterText={quickFilter}
          defaultColDef={defaultColDef}
          onRowClicked={handleOnRowClicked}
          getRowId={(params) => params.data?._id}
          groupDisplayType={"groupRows"}
          columnTypes={columnTypes}
          loading={loading}
          sideBar={sideBar}
        />
      </AgGridTableWrapper>
    );
  },
);

export default LoanQuickApplicationPipelineScreen;
